import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  fetchInsightConsents,
  fetchInsightVisits,
  fetchInsightsSummary,
} from '../analytics/client';
import { azureColors } from '../theme';

const TOKEN_KEY = 'portfolio_insights_token';

const StatCard = ({ label, value }) => (
  <Card sx={{ height: '100%', borderTop: `3px solid ${azureColors.primary}` }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ color: azureColors.primary, fontWeight: 700 }}>
        {value ?? 0}
      </Typography>
    </CardContent>
  </Card>
);

const CountTable = ({ title, rows, labelKey, valueKey = 'count' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((row, index) => (
              <TableRow key={`${row[labelKey]}-${index}`}>
                <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>{row[labelKey]}</TableCell>
                <TableCell align="right">{row[valueKey]}</TableCell>
              </TableRow>
            ))}
            {(!rows || rows.length === 0) && (
              <TableRow>
                <TableCell colSpan={2}>No data yet</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

const InsightsPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [inputToken, setInputToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [visits, setVisits] = useState([]);
  const [consents, setConsents] = useState([]);

  const unlocked = Boolean(token && summary);

  const loadData = async (adminToken) => {
    setLoading(true);
    setError('');
    try {
      const [summaryData, visitsData, consentsData] = await Promise.all([
        fetchInsightsSummary(adminToken),
        fetchInsightVisits(adminToken, { limit: 100 }),
        fetchInsightConsents(adminToken),
      ]);
      sessionStorage.setItem(TOKEN_KEY, adminToken);
      setToken(adminToken);
      setSummary(summaryData);
      setVisits(visitsData.rows || []);
      setConsents(consentsData.rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load insights');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = (event) => {
    event.preventDefault();
    loadData(inputToken.trim());
  };

  const handleLock = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setInputToken('');
    setSummary(null);
    setVisits([]);
    setConsents([]);
  };

  const stats = useMemo(() => summary?.summary || {}, [summary]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F3F2F1' }}>
      <Header />
      <Container sx={{ flex: 1, py: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ color: azureColors.primary }}>
          Site Insights
        </Typography>
        <Typography variant="body1" sx={{ color: azureColors.textSecondary, mb: 3, maxWidth: 720 }}>
          Private dashboard for visitor analytics stored in the local SQLite analytics service.
          Use the admin token from your Docker environment (`ANALYTICS_ADMIN_TOKEN`).
        </Typography>

        {!unlocked && (
          <Paper component="form" onSubmit={handleUnlock} sx={{ p: 3, maxWidth: 480, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Unlock insights</Typography>
            <TextField
              fullWidth
              type="password"
              label="Admin token"
              value={inputToken}
              onChange={(e) => setInputToken(e.target.value)}
              margin="normal"
              autoComplete="current-password"
            />
            <Button type="submit" variant="contained" disabled={loading || !inputToken.trim()} sx={{ mt: 1 }}>
              {loading ? 'Loading…' : 'View insights'}
            </Button>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {unlocked && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button variant="outlined" onClick={() => loadData(token)} disabled={loading}>
                Refresh
              </Button>
              <Button variant="text" onClick={handleLock}>Lock</Button>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Pageviews" value={stats.total_pageviews} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Sessions" value={stats.sessions} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Visitors" value={stats.visitors} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Unique IPs" value={stats.unique_ips} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Today" value={stats.pageviews_today} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Consents" value={stats.consent_records} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Accepted" value={stats.analytics_accepted} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard label="Rejected" value={stats.analytics_rejected} /></Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <CountTable title="Countries" rows={summary.byCountry} labelKey="country" />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountTable title="Pages" rows={summary.byPage} labelKey="page_path" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CountTable title="Browsers" rows={summary.byBrowser} labelKey="browser" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CountTable title="Operating systems" rows={summary.byOs} labelKey="os" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CountTable title="Devices" rows={summary.byDevice} labelKey="device_type" />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountTable title="Referrers" rows={summary.byReferrer} labelKey="referrer" />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountTable title="Last 30 days" rows={summary.byDay} labelKey="day" />
              </Grid>
            </Grid>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Recent visits</Typography>
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>When (UTC)</TableCell>
                        <TableCell>Path</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>IP</TableCell>
                        <TableCell>Browser</TableCell>
                        <TableCell>OS</TableCell>
                        <TableCell>Device</TableCell>
                        <TableCell>Referrer</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visits.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.created_at}</TableCell>
                          <TableCell>{row.page_path}</TableCell>
                          <TableCell>{row.country}</TableCell>
                          <TableCell>{row.city}</TableCell>
                          <TableCell>{row.ip}</TableCell>
                          <TableCell>{[row.browser, row.browser_version].filter(Boolean).join(' ')}</TableCell>
                          <TableCell>{[row.os, row.os_version].filter(Boolean).join(' ')}</TableCell>
                          <TableCell>{row.device_type}</TableCell>
                          <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.referrer || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Consent audit log</Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>When (UTC)</TableCell>
                        <TableCell>Allowed</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell>IP</TableCell>
                        <TableCell>Visitor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consents.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.created_at}</TableCell>
                          <TableCell>{row.analytics_allowed ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{row.consent_version}</TableCell>
                          <TableCell>{row.ip}</TableCell>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.visitor_id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default InsightsPage;
